/**
 * @file Floating UI widget for the 1auth batch transaction queue.
 *
 * Renders a fixed-position card anchored to the bottom-right of the viewport
 * that shows how many calls are queued and on which chain. The widget is
 * invisible while the queue is empty and mounts itself into the React tree
 * wherever `BatchQueueProvider` wraps the application.
 *
 * CSS keyframe animations are injected into `document.head` once on mount
 * via {@link injectStyles} so that no stylesheet import is required from the
 * consumer's bundler.
 */

import * as React from "react";
import { useBatchQueue, getChainName } from "./BatchQueueContext";

// batch-bounce-in: played on the whole widget when a new call is added to the
// queue — a subtle scale pop that draws attention without being distracting.
// batch-item-bounce: reserved for future per-item jiggle feedback (not
// currently triggered by default, available via inline animation assignment).
const ANIMATION_STYLES = `
@keyframes batch-bounce-in {
  0% { transform: scale(0.8); opacity: 0; }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes batch-item-bounce {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-2px); }
  75% { transform: translateX(2px); }
}
`;

/**
 * Inject the widget's CSS keyframe animations into `document.head`.
 *
 * Uses a stable element ID (`"batch-queue-widget-styles"`) as a guard so the
 * style tag is only inserted once even if the component mounts multiple times
 * (e.g. during React strict-mode double-invocation or hot reloads). No-ops in
 * non-browser environments (SSR/Node) where `document` is undefined.
 */
function injectStyles() {
  if (typeof document === "undefined") return;
  const styleId = "batch-queue-widget-styles";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = ANIMATION_STYLES;
  document.head.appendChild(style);
}

// Fixed bottom-right positioning keeps the widget out of the page flow so it
// never displaces content. z-index 50 sits above typical page elements but
// below full-screen modals (which usually start at 100+).
const widgetStyles: React.CSSProperties = {
  position: "fixed",
  bottom: "16px",
  right: "16px",
  zIndex: 50,
  backgroundColor: "#18181b",
  color: "#ffffff",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
  minWidth: "240px",
  maxWidth: "360px",
  fontFamily: "system-ui, -apple-system, sans-serif",
  fontSize: "14px",
  overflow: "hidden",
};

const headerStyles: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  cursor: "pointer",
  userSelect: "none",
};

const headerLeftStyles: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const counterBadgeStyles: React.CSSProperties = {
  backgroundColor: "#ffffff",
  color: "#18181b",
  borderRadius: "9999px",
  padding: "2px 8px",
  fontSize: "12px",
  fontWeight: 600,
};

const chainBadgeStyles: React.CSSProperties = {
  backgroundColor: "rgba(255,255,255,0.15)",
  color: "#a1a1aa",
  borderRadius: "4px",
  padding: "2px 6px",
  fontSize: "11px",
};

const signAllButtonStyles: React.CSSProperties = {
  backgroundColor: "#ffffff",
  color: "#18181b",
  border: "none",
  borderRadius: "6px",
  padding: "6px 12px",
  fontSize: "13px",
  fontWeight: 500,
  cursor: "pointer",
  transition: "background-color 0.15s",
};

const signAllButtonHoverStyles: React.CSSProperties = {
  backgroundColor: "#e4e4e7",
};

const signAllButtonDisabledStyles: React.CSSProperties = {
  opacity: 0.5,
  cursor: "not-allowed",
};

const listContainerStyles: React.CSSProperties = {
  maxHeight: "300px",
  overflowY: "auto",
  borderTop: "1px solid #27272a",
};

const callItemStyles: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  padding: "10px 16px",
  borderBottom: "1px solid #27272a",
  position: "relative",
};

const callItemLastStyles: React.CSSProperties = {
  borderBottom: "none",
};

const callContentStyles: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const callLabelStyles: React.CSSProperties = {
  fontWeight: 500,
  marginBottom: "2px",
};

const callSublabelStyles: React.CSSProperties = {
  color: "#a1a1aa",
  fontSize: "12px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const removeButtonStyles: React.CSSProperties = {
  position: "absolute",
  left: "8px",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "#dc2626",
  color: "#ffffff",
  border: "none",
  borderRadius: "4px",
  width: "20px",
  height: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: "14px",
  opacity: 0,
  transition: "opacity 0.15s",
};

const removeButtonVisibleStyles: React.CSSProperties = {
  opacity: 1,
};

const clearButtonStyles: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "10px 16px",
  backgroundColor: "transparent",
  color: "#a1a1aa",
  border: "none",
  borderTop: "1px solid #27272a",
  fontSize: "12px",
  cursor: "pointer",
  textAlign: "center",
  transition: "color 0.15s",
};

const clearButtonHoverStyles: React.CSSProperties = {
  color: "#ffffff",
};

/**
 * Animated chevron arrow used as the expand/collapse indicator in the widget
 * header. Points downward when the list is expanded and rotates 90° counter-
 * clockwise (points right) when collapsed, providing a clear affordance for
 * the toggle action.
 *
 * @param down - `true` when the queue list is expanded (chevron points down).
 */
function ChevronIcon({ down }: { down: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transition: "transform 0.2s",
        transform: down ? "rotate(0deg)" : "rotate(-90deg)",
      }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export interface BatchQueueWidgetProps {
  /** Callback when "Sign All" is clicked. */
  onSignAll: () => void;
}

/**
 * Floating widget that displays the current batch transaction queue.
 *
 * Renders nothing while the queue is empty. When calls are present it shows a
 * fixed card in the bottom-right corner of the viewport with:
 * - A count badge and optional chain name in the header.
 * - A "Sign All" button that invokes `onSignAll` to begin the signing ceremony.
 * - An expandable list of queued calls with per-item remove buttons revealed
 *   on hover and a "Clear batch" footer to discard all pending calls at once.
 *
 * A brief scale animation (`batch-bounce-in`) plays each time a new call is
 * added to the queue to draw the user's attention without interrupting flow.
 *
 * Must be rendered inside a `BatchQueueProvider`.
 *
 * @param props.onSignAll - Called when the user clicks "Sign All". The
 *   implementor is responsible for resolving the account identity and passing
 *   it to `useBatchQueue().signAll({ accountAddress })`.
 */
export function BatchQueueWidget({ onSignAll }: BatchQueueWidgetProps) {
  const {
    queue,
    batchChainId,
    removeFromBatch,
    clearBatch,
    isExpanded,
    setExpanded,
    isSigning,
    animationTrigger,
  } = useBatchQueue();

  const [hoveredItemId, setHoveredItemId] = React.useState<string | null>(null);
  const [isSignAllHovered, setIsSignAllHovered] = React.useState(false);
  const [isClearHovered, setIsClearHovered] = React.useState(false);
  const [shouldAnimate, setShouldAnimate] = React.useState(false);

  // Inject CSS animations on mount
  React.useEffect(() => {
    injectStyles();
  }, []);

  // Trigger bounce animation when item is added
  React.useEffect(() => {
    if (animationTrigger > 0) {
      setShouldAnimate(true);
      const timer = setTimeout(() => setShouldAnimate(false), 300);
      return () => clearTimeout(timer);
    }
  }, [animationTrigger]);

  // Don't render if queue is empty
  if (queue.length === 0) {
    return null;
  }

  const handleHeaderClick = () => {
    setExpanded(!isExpanded);
  };

  const handleSignAllClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't toggle expand
    if (!isSigning) {
      onSignAll();
    }
  };

  const animatedWidgetStyles: React.CSSProperties = {
    ...widgetStyles,
    animation: shouldAnimate ? "batch-bounce-in 0.3s ease-out" : undefined,
  };

  const currentSignAllStyles: React.CSSProperties = {
    ...signAllButtonStyles,
    ...(isSignAllHovered && !isSigning ? signAllButtonHoverStyles : {}),
    ...(isSigning ? signAllButtonDisabledStyles : {}),
  };

  return (
    <div style={animatedWidgetStyles}>
      {/* Header - always visible */}
      <div style={headerStyles} onClick={handleHeaderClick}>
        <div style={headerLeftStyles}>
          <ChevronIcon down={isExpanded} />
          <span style={counterBadgeStyles}>{queue.length}</span>
          <span>call{queue.length !== 1 ? "s" : ""} queued</span>
          {batchChainId && (
            <span style={chainBadgeStyles}>{getChainName(batchChainId)}</span>
          )}
        </div>
        <button
          style={currentSignAllStyles}
          onClick={handleSignAllClick}
          onMouseEnter={() => setIsSignAllHovered(true)}
          onMouseLeave={() => setIsSignAllHovered(false)}
          disabled={isSigning}
        >
          {isSigning ? "Signing..." : "Sign All"}
        </button>
      </div>

      {/* Expanded list of calls */}
      {isExpanded && (
        <>
          <div style={listContainerStyles}>
            {queue.map((item, index) => {
              const isHovered = hoveredItemId === item.id;
              const isLast = index === queue.length - 1;

              return (
                <div
                  key={item.id}
                  style={{
                    ...callItemStyles,
                    ...(isLast ? callItemLastStyles : {}),
                    paddingLeft: isHovered ? "36px" : "16px",
                    transition: "padding-left 0.15s",
                  }}
                  onMouseEnter={() => setHoveredItemId(item.id)}
                  onMouseLeave={() => setHoveredItemId(null)}
                >
                  <button
                    style={{
                      ...removeButtonStyles,
                      ...(isHovered ? removeButtonVisibleStyles : {}),
                    }}
                    onClick={() => removeFromBatch(item.id)}
                    title="Remove from batch"
                  >
                    &times;
                  </button>
                  <div style={callContentStyles}>
                    <div style={callLabelStyles}>
                      {item.call.label || "Contract Call"}
                    </div>
                    {item.call.sublabel && (
                      <div style={callSublabelStyles}>{item.call.sublabel}</div>
                    )}
                    {!item.call.sublabel && item.call.to && (
                      <div style={callSublabelStyles}>
                        To: {item.call.to.slice(0, 6)}...{item.call.to.slice(-4)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Clear batch button */}
          <button
            style={{
              ...clearButtonStyles,
              ...(isClearHovered ? clearButtonHoverStyles : {}),
            }}
            onClick={clearBatch}
            onMouseEnter={() => setIsClearHovered(true)}
            onMouseLeave={() => setIsClearHovered(false)}
          >
            Clear batch
          </button>
        </>
      )}
    </div>
  );
}
